/**
 * @pg/core: what every PetGuru module shares.
 *
 * - `config`: routes, money format, strings and API URLs, read once from
 *   <script type="application/json" id="pg-config"> (snippets/pg-scripts.liquid).
 * - `PgElement`: a small custom-element base. It collects `ref="name"` children into
 *   `this.refs` (arrays for `ref="name[]"`) and gives every instance an AbortSignal that is
 *   aborted on disconnect, so listeners registered with `{ signal: this.signal }` clean
 *   themselves up.
 * - Helpers: formatMoney, debounce, t (strings), toast.
 */

const configEl = document.getElementById('pg-config');

/** @type {Record<string, any>} */
export const config = configEl ? JSON.parse(configEl.textContent || '{}') : {};

export class PgElement extends HTMLElement {
  /** @type {Record<string, any>} */
  refs = {};
  #controller = new AbortController();

  get signal() {
    return this.#controller.signal;
  }

  connectedCallback() {
    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    this.collectRefs();
    this.mount?.();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.unmount?.();
  }

  /** Re-read refs after this element's markup was replaced (e.g. by the Section Rendering API). */
  collectRefs() {
    /** @type {Record<string, any>} */
    const refs = {};
    for (const el of this.querySelectorAll('[ref]')) {
      // A ref belongs to its nearest enclosing pg-* element, so nested components keep their own.
      if (ownerOf(el) !== this) continue;
      const name = el.getAttribute('ref') || '';
      if (name.endsWith('[]')) {
        (refs[name.slice(0, -2)] ||= []).push(el);
      } else {
        refs[name] = el;
      }
    }
    this.refs = refs;
  }

  /** Dispatch a bubbling CustomEvent from this element. */
  emit(type, detail) {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, detail }));
  }
}

/** @param {Element} el */
function ownerOf(el) {
  let node = el.parentElement;
  while (node && !node.localName.startsWith('pg-')) node = node.parentElement;
  return node;
}

/**
 * Define a custom element once (modules can be imported from several sections).
 * @param {string} name
 * @param {CustomElementConstructor} constructor
 */
export function define(name, constructor) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}

/**
 * Format cents with the shop's money format, e.g. "{{amount_with_comma_separator}} lei".
 * @param {number|string} cents
 * @param {string} [format]
 */
export function formatMoney(cents, format = config.moneyFormat || '{{amount}}') {
  const value = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : Math.round(cents);
  const fmt = (n, decimals, thousands = ',', decimal = '.') => {
    const [int, frac] = (n / 100).toFixed(decimals).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return fmt(value, 0);
      case 'amount_with_comma_separator':
        return fmt(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return fmt(value, 0, '.');
      case 'amount_with_apostrophe_separator':
        return fmt(value, 2, "'");
      case 'amount_with_space_separator':
        return fmt(value, 2, ' ', ',');
      default:
        return fmt(value, 2);
    }
  });
}

/**
 * @template {(...args: any[]) => void} F
 * @param {F} fn
 * @param {number} wait
 */
export function debounce(fn, wait = 250) {
  /** @type {number|undefined} */
  let timer;
  return /** @type {F} */ (
    (...args) => {
      clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), wait);
    }
  );
}

/**
 * Translated string from config.strings, with {{ placeholder }} interpolation.
 * @param {string} key
 * @param {Record<string, string|number>} [vars]
 */
export function t(key, vars = {}) {
  const value = config.strings?.[key] ?? key;
  return value.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => String(vars[name] ?? ''));
}

/**
 * Show a short status message. The region is a polite live region in layout/theme.liquid.
 * @param {string} message
 * @param {{ icon?: string, duration?: number, onClick?: () => void }} [options]
 */
export function toast(message, { icon = 'check-circle', duration = 3200, onClick } = {}) {
  const region = document.getElementById('pg-toasts');
  const template = /** @type {HTMLTemplateElement|null} */ (document.getElementById(`pg-icon-${icon}`));
  if (!region) return;
  const el = document.createElement(onClick ? 'button' : 'div');
  el.className = 'pg-toast';
  if (template) el.append(template.content.cloneNode(true));
  el.append(document.createTextNode(message));
  if (onClick) el.addEventListener('click', onClick, { once: true });
  region.append(el);
  setTimeout(() => {
    el.classList.add('is-leaving');
    el.addEventListener('animationend', () => el.remove(), { once: true });
    // Reduced motion: no animationend fires for a 0.01ms animation in some browsers.
    setTimeout(() => el.remove(), 400);
  }, duration);
}

/** True when the user asked for less motion. */
export const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Run a DOM update inside a same-document View Transition when supported and motion is allowed.
 * @param {() => void | Promise<void>} update
 */
export function withTransition(update) {
  if (!document.startViewTransition || reducedMotion()) return Promise.resolve(update());
  return document.startViewTransition(update).updateCallbackDone;
}
